import { Box, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { EmailVerificationType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { logError } from '@zg-rentals/log-error';

import { EmailVerificationSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import propertyUtils from '../../../utils/propertyUtils';

const failedVerificationMessage =
  'This account doesn’t match account provided on listing. For additional help or questions, please contact customer support.';
const needVerificationMessage = 'We found additional listings associated with your email address.';

type Props = {
  handleResendEmailClick: (notificationText: string) => void;
  failedVerification: boolean;
  onClose: () => void;
};

const VerifyEmailModal = ({ handleResendEmailClick, failedVerification, onClose }: Props): JSX.Element => {
  const { email, isPostingPathAdmin } = useSelector((state) => UserSelectors.makeUser(state));
  const { inactiveListings, activeListings } = useSelector((state) =>
    EmailVerificationSelectors.makeEmailVerification(state),
  );
  const { mutateAsync: sendSecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken();

  const notificationText = `Email resent. The additional listings associated with your account will be added once you
              verify your email at ${email}.`;

  const onResendEmailClick = async () => {
    if (isPostingPathAdmin) {
      handleResendEmailClick(notificationText);
      onClose();
    } else {
      try {
        await sendSecurityToken({
          getParams: { emailVerificationType: EmailVerificationType.ACCOUNT_EMAIL_CLAIM_LISTINGS },
        });
        handleResendEmailClick(notificationText);
        onClose();
      } catch (error) {
        logError({
          error,
          context: 'onResendEmailClick',
        });
      }
    }
  };

  const renderAddress = (listing: Record<string, string>, i: number) => {
    const { formattedCompleteAddress } = propertyUtils.formatAddress(listing);
    return (
      <Box css={{ marginBlock: '200' }} key={i} data-testid="address-list-item">
        <Text asChild>
          <p>{formattedCompleteAddress}</p>
        </Text>
      </Box>
    );
  };

  return (
    <Modal
      header={<Heading level={2}>{`${failedVerification ? 'Incorrect account' : 'Listings found'}`}</Heading>}
      body={
        <Box css={{ paddingBottom: '400' }}>
          <Text>{failedVerification ? failedVerificationMessage : needVerificationMessage}</Text>
          {!failedVerification && (
            <>
              <Box css={{ marginBlock: '400' }}>
                <Text data-testid="email-text">
                  We&apos;ve sent an email to <b>{email}</b>. Select the link in the email to confirm your email address
                  and access those listings in Zillow Rental Manager. 
                  <TextButton onClick={onResendEmailClick} textStyle="body">
                    Resend email
                  </TextButton>
                </Text>
              </Box>
              {activeListings.length > 0 && (
                <Box css={{ marginBlock: '200' }}>
                  <Text textStyle="body-bold" data-testid="active-listings" asChild>
                    <p>Active Listings</p>
                  </Text>
                  {activeListings.map((listing: Record<string, string>, i: number) => renderAddress(listing, i))}
                </Box>
              )}

              {inactiveListings.length > 0 && (
                <Box css={{ marginBlock: '200' }}>
                  <Text data-testid="inactive-listings" textStyle="body-bold" asChild>
                    <p>Inactive Listings</p>
                  </Text>
                  {inactiveListings.map((listing: Record<string, string>, i: number) => renderAddress(listing, i))}
                </Box>
              )}
            </>
          )}
        </Box>
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default VerifyEmailModal;
